import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { onFocusSelect } from '@/hooks';
import { Button } from '@/primitives/Button';
import { Copy, Edit as EditIcon, Icon } from '@/primitives/Icons';
import { Input } from '@/primitives/Input';
import { copyToClipboard } from '@/utils/misc';

import { useWindowLoaderData, useWindowStore } from '../api/windowStore';
import styles from './OrderTitle.module.css';
import { StatusBadge } from './StatusBadge';

export const OrderTitle = ({ readonly = false }: { readonly?: boolean }) => {
  const order = useWindowLoaderData();
  const { t } = useTranslation();
  const { title, setTitle } = useWindowStore();
  const { shortId } = order;

  const [isEditing, setIsEditing] = useState(false);
  const inputId = useId();

  useEffect(() => {
    if (isEditing) {
      document.getElementById(inputId)?.focus();
    }
  }, [isEditing, inputId]);

  return (
    <div className={styles.layout} style={{ paddingBottom: 'var(--space-m)' }}>
      <div></div>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-xxs)',
        }}
      >
        {shortId ? shortId : ''}
        {shortId && (
          <Button
            style={{
              color: 'var(--gray-8)',
              outline: 'none',
              boxShadow: 'none',
              border: 'none',
            }}
            variant="icon"
            icon={<Icon size={2} icon={<Copy />} />}
            onClick={async () => {
              await copyToClipboard(shortId);
              toast.success(t('reseller.orderTitle.copiedToClipboard'));
            }}
          />
        )}
      </span>
      <div
        style={{
          fontSize: '1rem',
          fontWeight: '600',
          padding: 'var(--space-xxs) var(--space-s)',
          alignSelf: 'center',
        }}
      >
        <StatusBadge
          order={order}
          style={{ padding: 'var(--space-xxs) var(--space-s)' }}
        />
      </div>
      <div style={{ flex: 1 }}>
        <form name="order-title-form">
          {readonly || !isEditing ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid transparent',
              }}
            >
              <h1
                onClick={() => setIsEditing(!readonly && true)}
                className={styles.orderTitle}
                style={{
                  color: title ? undefined : 'var(--gray-7)',
                }}
              >
                {title || t('reseller.orderTitle.placeholder')}
              </h1>
              {!readonly && (
                <Button
                  style={{
                    color: 'var(--primary)',
                  }}
                  onClick={() => setIsEditing(true)}
                  variant="icon"
                  icon={<Icon size={3} icon={<EditIcon />} />}
                />
              )}
            </div>
          ) : (
            <Input
              value={title}
              className="title"
              placeholder={t('reseller.orderTitle.placeholder')}
              id={inputId}
              onFocus={onFocusSelect}
              onChange={(e) => {
                setTitle(e.currentTarget.value);
              }}
              maxLength={80}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsEditing(false);
                  e.preventDefault();
                  return false;
                }
              }}
              onBlur={() => setIsEditing(false)}
              required
            />
          )}
        </form>
      </div>
    </div>
  );
};
